import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import { useToast } from '@/hooks/use-toast';
import { useOfferStore } from '@/lib/store';
import { runWithConcurrencyLimit } from '@/lib/concurrency-limit';
import {
  buildBatchApplyPlan,
  buildBatchCatalogRows,
  buildBatchSearchQuery,
  classifyMaterialsSearchResponse,
  countBatchOutcomes,
  ermittleEffektivenBatchAufschlag,
  formatBatchAufschlagHinweis,
  isBatchEligiblePosition,
  type BatchCatalogRow,
  type BatchPositionSource,
  type CatalogSearchCandidate,
  type MaterialsSearchAttemptResult,
} from '@shared/catalog-batch-suggest';
import {
  canCommitOfferDocumentTask,
  commitOfferDocumentWriteIfCurrent,
  selectSuccessfullyAppliedValues,
  transitionCatalogBatchApply,
} from '@shared/batch-apply-ui-controller';
import type { CatalogMatchZone } from '@shared/catalog-position-match';
import { bewertePreisFrische, type PreisFrischeStufe } from '@shared/preis-frische';
import { AlertTriangle, Loader2, PackageSearch, Upload } from 'lucide-react';

// Preisfrische-Badge (shared/preis-frische.ts) — dieselben Stufen/Farben wie im
// Einzeldialog (catalog-suggest-dialog.tsx), damit beide Dialoge identisch sprechen.
const FRISCHE_BADGE_CLASS: Record<PreisFrischeStufe, string> = {
  frisch: 'border-emerald-300 bg-emerald-50 text-emerald-700',
  aelter: 'border-amber-300 bg-amber-50 text-amber-700',
  abgelaufen: 'border-red-300 bg-red-50 text-red-700',
  unbekannt: 'border-gray-200 bg-gray-50 text-gray-500',
};

/** Maximal gleichzeitig laufende Suchanfragen — siehe client/src/lib/concurrency-limit.ts. */
const SEARCH_CONCURRENCY_LIMIT = 5;

const ZONE_BADGE_CLASS: Record<CatalogMatchZone, string> = {
  high: 'bg-emerald-600 hover:bg-emerald-600',
  medium: 'bg-amber-500 hover:bg-amber-500',
  low: 'bg-gray-400 hover:bg-gray-400',
};

const ZONE_LABEL: Record<CatalogMatchZone, string> = {
  high: 'hoch',
  medium: 'mittel',
  low: 'niedrig',
};

function formatEuro(value: number): string {
  return value.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

/**
 * P2-G: liefert NICHT mehr nur ein Kandidaten-Array, sondern unterscheidet einen
 * fehlgeschlagenen Request (Netzwerkfehler, abgelaufene Session, 5xx) von einem echten
 * "kein Treffer" (Server hat erfolgreich mit einer leeren Liste geantwortet). Die
 * eigentliche Klassifizierung ist reine, testbare Logik in
 * shared/catalog-batch-suggest.ts::classifyMaterialsSearchResponse — hier bleibt nur der
 * HTTP-Call selbst. Eine einzelne fehlgeschlagene Suche blockiert weiterhin NICHT die
 * übrigen Positionen (isolierte Fehler werden toleriert), landet aber sichtbar als
 * "suche-fehlgeschlagen" statt lautlos als "kein Treffer".
 */
// P1-2 (Fix, Codex-Fund): die Route sortiert alphabetisch und begrenzt DANN auf `limit` — das
// Ranking (rankCatalogCandidates) läuft komplett client-seitig auf genau den zurückgelieferten
// Kandidaten. Ein zu kleines Limit (vorher 10) kann den eigentlich besten Treffer außerhalb des
// abgeschnittenen alphabetischen Präfix verstecken. Fix: das MAXIMAL erlaubte Server-Limit (50)
// anfragen, damit Trunkierung in der Praxis die Ausnahme bleibt — zusätzlich meldet der Server
// über `truncated`, ob selbst das noch nicht gereicht hat (siehe classifyMaterialsSearchResponse
// + buildBatchCatalogRow, das die automatische Vorauswahl in diesem Fall hart deaktiviert).
const SEARCH_RESULT_LIMIT = 50;

// P2-3 (Fix, Codex-Fund): `signal` durchgereicht, damit ein bereits LAUFENDER Request bei einer
// Invalidierung (Dialog neu geöffnet/geschlossen, Angebot gewechselt) tatsächlich abgebrochen
// wird, statt nur sein (dann ignoriertes) Ergebnis zu verwerfen — siehe
// client/src/lib/concurrency-limit.ts::RunWithConcurrencyLimitOptions.signal. Ein Abort landet
// hier im catch-Zweig wie jeder andere Netzwerkfehler (als "Suche fehlgeschlagen" klassifiziert)
// — für eine ohnehin verworfene, veraltete Batch-Suche ist das irrelevant.
async function searchMaterialsForPosition(query: string, signal?: AbortSignal): Promise<MaterialsSearchAttemptResult> {
  const trimmed = query.trim();
  if (trimmed.length === 0) return { ok: true, candidates: [], truncated: false };
  try {
    const response = await fetch(
      `/api/softec-db/materials/search?q=${encodeURIComponent(trimmed)}&limit=${SEARCH_RESULT_LIMIT}`,
      { signal },
    );
    if (!response.ok) return classifyMaterialsSearchResponse({ ok: false, data: null });
    const data = await response.json();
    return classifyMaterialsSearchResponse({ ok: true, data });
  } catch {
    return classifyMaterialsSearchResponse({ error: true });
  }
}

/**
 * Batch-Modus des Katalog-Vorschlags: "Suche zu ALLEN meinen LV-Positionen automatisch die
 * passenden Großhandelsartikel und schlage sie vor." (Ollis Wunsch). Spiegelt die
 * Vorschau/Bestätigen-Struktur von client/src/components/gaeb/X84PriceImport.tsx, aber als
 * Suchquelle dient GET /api/softec-db/materials/search (wie im Einzel-Dialog
 * catalog-suggest-dialog.tsx) statt einer hochgeladenen X84-Datei.
 *
 * Such-Weg (Begründung): N konkurrenzbegrenzte Aufrufe der BESTEHENDEN Such-Route, statt eines
 * neuen Bulk-Endpunkts. Kein neuer Server-Code/keine neue Angriffsfläche (Auth/Gating der Route
 * bleibt exakt wie beim Einzel-Dialog), keine DB-Änderung — der einzig neue Client-Baustein ist
 * die Nebenläufigkeitsgrenze (client/src/lib/concurrency-limit.ts, Default 5 gleichzeitige
 * Requests), damit ein großes LV nicht N gleichzeitige Requests abfeuert.
 *
 * ALLE Gating-/Auswahl-Entscheidungen (Konfidenz-Vorauswahl, Blockieren einer Bestandskalkulation,
 * Sub-Cent-Schutz, Bulk-Übernahme-Plan) laufen über shared/catalog-batch-suggest.ts — diese
 * Komponente holt nur Rohdaten und rendert sie.
 */
export function CatalogBatchSuggestDialog() {
  const positions = useOfferStore((s) => s.positions);
  const quoteItems = useOfferStore((s) => s.quoteItems);
  const kalkulationsDefaults = useOfferStore((s) => s.kalkulationsDefaults);
  const updatePositionKalkulation = useOfferStore((s) => s.updatePositionKalkulation);
  // Die sessionlokale Dokument-Epoch unterscheidet auch Reloads mit identischer DB-/Slot-ID.
  const offerDocumentEpoch = useOfferStore((s) => s.offerDocumentEpoch);
  const { toast } = useToast();

  const [isOpen, setIsOpen] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [rows, setRows] = useState<BatchCatalogRow[] | null>(null);
  const [rowsDocumentEpoch, setRowsDocumentEpoch] = useState<number | null>(null);
  const [checkedIds, setCheckedIds] = useState<Set<number>>(new Set());
  const [rejectedIds, setRejectedIds] = useState<Set<number>>(new Set());
  const [isApplying, setIsApplying] = useState(false);

  // P1-5: Generation-Zähler gegen veraltete Batch-Suchergebnisse (siehe
  // shared/catalog-position-match.ts::istAktuelleGeneration /
  // shared/catalog-batch-suggest.ts::sollBatchErgebnisUebernehmen). Wird bei JEDEM neuen
  // Suchstart, beim Schließen des Dialogs UND bei einem Angebotswechsel erhöht — eine ältere,
  // noch laufende Invocation erkennt daran, dass ihre Ergebnisse verworfen werden müssen,
  // statt neuere (evtl. zwischenzeitlich editierte) Zeilen zu überschreiben.
  const generationRef = useRef(0);
  const lastProcessedOfferDocumentEpochRef = useRef(offerDocumentEpoch);
  // P2-3 (Fix, Codex-Fund): der Generation-Guard unterdrückte bisher NUR State-Updates einer
  // veraltet gewordenen Batch-Suche — die Worker in runWithConcurrencyLimit dequeueten und
  // fetchten trotzdem ALLE restlichen Positionen weiter (Server-Last durch hunderte unnötige
  // Requests, plus ein zweiter, paralleler Batch bei erneutem Öffnen). Dieser AbortController
  // wird bei jedem neuen Suchstart NEU angelegt und beim Invalidieren (Schließen/Angebotswechsel/
  // neuer Suchstart) abgebrochen — zusätzlich zum `shouldContinue`-Check unten, der verhindert,
  // dass NEUE Worker-Läufe überhaupt erst gestartet werden.
  const abortControllerRef = useRef<AbortController | null>(null);

  // Jede echte Dokumentgrenze invalidiert/abortet den Lauf und leert den kompletten Dialogzustand.
  useEffect(() => {
    if (lastProcessedOfferDocumentEpochRef.current === offerDocumentEpoch) return;
    lastProcessedOfferDocumentEpochRef.current = offerDocumentEpoch;
    generationRef.current += 1;
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    setIsOpen(false);
    setRows(null);
    setRowsDocumentEpoch(null);
    setCheckedIds(new Set());
    setRejectedIds(new Set());
    setIsSearching(false);
    setIsApplying(false);
  }, [offerDocumentEpoch]);

  const mengeById = useMemo(() => {
    const map = new Map<number, number>();
    for (const item of quoteItems) {
      const menge = Number(item.menge);
      map.set(item.id, Number.isFinite(menge) ? menge : 0);
    }
    return map;
  }, [quoteItems]);

  const positionById = useMemo(() => {
    const map = new Map<number, (typeof positions)[number]>();
    for (const p of positions) map.set(p.id, p);
    return map;
  }, [positions]);

  const runBatchSearch = async () => {
    // P1-5: eigene Generation für DIESE Invocation — jeder neue Aufruf (Dialog neu geöffnet)
    // erhöht den Zähler, wodurch eine evtl. noch laufende ÄLTERE Invocation beim Fertigwerden
    // erkennt, dass sie veraltet ist (siehe Guards unten).
    generationRef.current += 1;
    const startGeneration = generationRef.current;
    const startOfferDocumentEpoch = useOfferStore.getState().offerDocumentEpoch;
    // P2-3: jeder neue Suchstart bricht einen evtl. noch laufenden ÄLTEREN Batch (dessen
    // Requests) ab UND bekommt seinen eigenen Controller für die Requests DIESES Laufs.
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const eligible = positions.filter((p) =>
      isBatchEligiblePosition({
        type: (p as any).type ?? null,
        name: p.name,
        beschreibung: p.beschreibung,
        menge: mengeById.get(p.id) ?? 0,
      }),
    );

    setIsSearching(true);
    setRows(null);
    setRowsDocumentEpoch(null);
    setCheckedIds(new Set());
    setRejectedIds(new Set());
    try {
      const sources = await runWithConcurrencyLimit(
        eligible,
        SEARCH_CONCURRENCY_LIMIT,
        async (position, _index, signal) => {
          // buildBatchSearchQuery kappt die ausgewählte Quelle (name oder beschreibung) — ein
          // GAEB/PDF-Import kann diese sonst mit einer unbegrenzten Langbeschreibung füllen, die
          // ungekappt in searchMaterialsForPosition() als GET-Query landet und das Proxy-/
          // Node-Request-Target-Limit sprengen kann, bevor die Route überhaupt läuft.
          const query = buildBatchSearchQuery(position.name, position.beschreibung);
          const attempt = await searchMaterialsForPosition(query, signal);
          const source: BatchPositionSource = {
            positionId: position.id,
            position: {
              name: position.name ?? '',
              beschreibung: position.beschreibung,
              einheit: position.einheit,
              artikelnummer: null,
            },
            kalkulationBestehend: position.kalkulation ?? null,
            candidates: attempt.candidates,
            searchFailed: !attempt.ok,
            truncated: attempt.truncated,
          };
          return source;
        },
        {
          // P2-3: vor JEDEM neu einzureihenden Fetch prüfen, ob DIESER Lauf noch aktuell ist —
          // stale (Dialog neu geöffnet/geschlossen, Angebot gewechselt) startet keine weiteren
          // Requests mehr, statt die komplette Restliste noch abzuarbeiten.
          shouldContinue: () =>
            generationRef.current === startGeneration &&
            useOfferStore.getState().offerDocumentEpoch === startOfferDocumentEpoch,
          signal: controller.signal,
        },
      );

      // P1-5: veraltete Antwort verwerfen — Dialog wurde zwischenzeitlich geschlossen/neu
      // geöffnet oder das Angebot gewechselt (generationRef wurde dadurch bereits erhöht).
      if (!canCommitOfferDocumentTask({
        startGeneration,
        currentGeneration: generationRef.current,
        expectedDocumentEpoch: startOfferDocumentEpoch,
        currentDocumentEpoch: useOfferStore.getState().offerDocumentEpoch,
      })) return;

      const builtRows = buildBatchCatalogRows(sources);
      setRows(builtRows);
      setRowsDocumentEpoch(startOfferDocumentEpoch);
      setCheckedIds(new Set(builtRows.filter((r) => r.defaultChecked).map((r) => r.positionId)));
    } finally {
      if (canCommitOfferDocumentTask({
        startGeneration,
        currentGeneration: generationRef.current,
        expectedDocumentEpoch: startOfferDocumentEpoch,
        currentDocumentEpoch: useOfferStore.getState().offerDocumentEpoch,
      })) {
        setIsSearching(false);
      }
    }
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open) {
      void runBatchSearch();
    } else {
      generationRef.current += 1; // P1-5: laufende Suche beim Schließen invalidieren
      abortControllerRef.current?.abort(); // P2-3: laufende Requests beim Schließen abbrechen
      setRows(null);
      setRowsDocumentEpoch(null);
      setCheckedIds(new Set());
      setRejectedIds(new Set());
      setIsSearching(false);
      setIsApplying(false);
    }
  };

  const toggleChecked = (positionId: number, checked: boolean) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(positionId);
      else next.delete(positionId);
      return next;
    });
  };

  const counts = useMemo(() => (rows ? countBatchOutcomes(rows) : null), [rows]);
  const checkedApplicableCount = useMemo(
    () => (rows ? rows.filter((r) => r.outcome === 'uebernehmbar' && checkedIds.has(r.positionId)).length : 0),
    [rows, checkedIds],
  );

  const handleApply = () => {
    if (!rows) return;
    if (
      rowsDocumentEpoch === null ||
      useOfferStore.getState().offerDocumentEpoch !== rowsDocumentEpoch
    ) {
      handleOpenChange(false);
      return;
    }
    setIsApplying(true);
    try {
      const plan = buildBatchApplyPlan(rows, checkedIds);
      const attemptedPositionIds = plan.map((entry) => entry.positionId);
      const applyResults: boolean[] = [];
      for (const entry of plan) {
        const write = commitOfferDocumentWriteIfCurrent({
          expectedDocumentEpoch: rowsDocumentEpoch,
          getCurrentDocumentEpoch: () => useOfferStore.getState().offerDocumentEpoch,
          write: () => updatePositionKalkulation(entry.positionId, entry.kalkulation),
        });
        if (!write.committed) {
          handleOpenChange(false);
          return;
        }
        applyResults.push(write.value);
      }
      const angewendeteKalkulationen = selectSuccessfullyAppliedValues(
        plan.map((entry) => entry.kalkulation),
        applyResults,
      );
      const transition = transitionCatalogBatchApply({
        visiblePositionIds: rows.map((row) => row.positionId),
        checkedPositionIds: [...checkedIds],
        attemptedPositionIds,
        applyResults,
      });

      if (!transition.shouldCloseDialog) {
        const visible = new Set(transition.visiblePositionIds);
        setRows((current) =>
          current ? current.filter((row) => visible.has(row.positionId)) : current,
        );
        setCheckedIds(new Set(transition.checkedPositionIds));
        setRejectedIds(new Set(transition.rejectedPositionIds));
      }

      const uebersprungen = checkedIds.size - plan.length;
      const uebersprungenHinweis =
        uebersprungen > 0 ? ` ${uebersprungen} angehakte Position(en) übersprungen (blockiert/ohne gültigen Treffer).` : '';
      // P2-3 (Fix, Codex-Review gpt-5.6): der Toast meldete bisher IMMER pauschal den GLOBALEN
      // Default-Aufschlag, obwohl eine angehakte Position mit Positions-/blitz-material-Override
      // real einen anderen Aufschlag gerechnet bekommt (der Preis war korrekt, die Meldung nicht).
      // Fix: den TATSÄCHLICH effektiv genutzten Aufschlag ermitteln (shared/catalog-batch-
      // suggest.ts::ermittleEffektivenBatchAufschlag, derselbe Pfad wie die Kalkulation selbst) —
      // bei uneinheitlichen Overrides ein ehrlicher Sammelhinweis statt einer pauschal falschen Zahl.
      const aufschlagSummary = ermittleEffektivenBatchAufschlag(angewendeteKalkulationen, kalkulationsDefaults);
      const aufschlagHinweis = formatBatchAufschlagHinweis(aufschlagSummary);
      // Leerer Hinweis NUR, wenn keine Position tatsächlich angewendet wurde (leere Liste) —
      // dann macht eine Klammer ohne Inhalt keinen Sinn (siehe formatBatchAufschlagHinweis).
      const aufschlagKlammer = aufschlagHinweis ? ` (${aufschlagHinweis})` : '';
      const title =
        transition.toastKind === 'success'
          ? 'Katalogpreise übernommen'
          : transition.toastKind === 'partial'
            ? 'Katalogpreise teilweise übernommen'
            : 'Keine Preise übernommen';
      toast({
        title,
        description:
          `${transition.appliedCount} Position(en) mit Material-EK aktualisiert${aufschlagKlammer}.` +
          uebersprungenHinweis +
          (transition.rejectedCount > 0
            ? ` ${transition.rejectedCount} Position(en) abgelehnt — bitte einzeln prüfen.`
            : ''),
        variant: transition.shouldCloseDialog ? undefined : 'destructive',
      });
      if (transition.shouldCloseDialog) {
        handleOpenChange(false);
      }
    } finally {
      setIsApplying(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button type="button" variant="outline" size="sm" className="h-8">
          <PackageSearch className="mr-1.5 h-3.5 w-3.5" />
          Katalog: alle Positionen
        </Button>
      </DialogTrigger>

      <DialogContent className="sm:max-w-4xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <PackageSearch className="h-5 w-5 text-emerald-600" />
            <span>Katalog-Vorschlag für alle Positionen</span>
          </DialogTitle>
          <DialogDescription>
            Sucht für jede LV-Position passende Großhandelsartikel und schlägt sie vor. Nur hohe
            Konfidenz ist vorausgewählt — bitte prüfen und bei Bedarf weitere Positionen anhaken.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto space-y-3 py-1">
          {isSearching && (
            <div className="flex items-center justify-center text-sm text-gray-500 py-8">
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Suche läuft für alle Positionen...
            </div>
          )}

          {!isSearching && rows && counts && (
            <>
              <div className="flex items-center gap-2 text-sm flex-wrap">
                <Badge className="bg-emerald-600">{counts.uebernehmbar} übernehmbar</Badge>
                {counts.blockiert > 0 && (
                  <Badge variant="outline" className="border-amber-300 text-amber-700">
                    {counts.blockiert} blockiert
                  </Badge>
                )}
                {counts.keinTreffer > 0 && (
                  <Badge variant="secondary">{counts.keinTreffer} ohne Treffer</Badge>
                )}
                {counts.sucheFehlgeschlagen > 0 && (
                  <Badge variant="outline" className="border-red-300 text-red-700">
                    {counts.sucheFehlgeschlagen} Suche fehlgeschlagen
                  </Badge>
                )}
              </div>

              {/* P2-G: aggregierte Fehlermeldung, wenn (Teile der) Suche fehlgeschlagen sind —
                  isolierte Einzelfehler werden toleriert (die übrigen Positionen bleiben
                  nutzbar), aber ein Ausfall darf nie unsichtbar bleiben. */}
              {counts.sucheFehlgeschlagen > 0 && (
                <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">
                  <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>
                    {counts.sucheFehlgeschlagen} von {rows.length} Position(en): Suche fehlgeschlagen
                    (Netzwerk-/Serverfehler).{' '}
                    {counts.sucheFehlgeschlagen === rows.length
                      ? 'Keine Position konnte durchsucht werden — bitte erneut versuchen.'
                      : 'Diese Positionen bitte einzeln prüfen oder die Suche erneut ausführen.'}
                  </span>
                </div>
              )}

              {rows.length === 0 ? (
                <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-3">
                  Keine batch-fähigen Positionen gefunden (Titel/Zwischensummen zählen nicht).
                </div>
              ) : (
                <div className="border rounded-md overflow-hidden">
                  <div className="max-h-[50vh] overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr className="text-left text-xs text-gray-600">
                          <th className="px-2 py-1.5 font-medium w-[36px]"></th>
                          <th className="px-2 py-1.5 font-medium">Position</th>
                          <th className="px-2 py-1.5 font-medium">Vorschlag</th>
                          <th className="px-2 py-1.5 font-medium text-right">Eff. EK</th>
                          <th className="px-2 py-1.5 font-medium">Konfidenz</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((row) => {
                          const position = positionById.get(row.positionId);
                          const raw = row.best?.candidate as CatalogSearchCandidate | undefined;
                          const disabled = row.outcome !== 'uebernehmbar';
                          // Preisfrische je Vorschlag — 'unbekannt' zeigt bewusst kein Badge.
                          const frische = raw ? bewertePreisFrische(raw, new Date()) : null;
                          return (
                            <tr
                              key={row.positionId}
                              className={`border-t ${
                                row.outcome === 'blockiert'
                                  ? 'bg-amber-50'
                                  : row.outcome === 'suche-fehlgeschlagen'
                                    ? 'bg-red-50 text-red-600'
                                    : row.outcome === 'kein-treffer'
                                      ? 'bg-gray-50 text-gray-400'
                                      : ''
                              }`}
                            >
                              <td className="px-2 py-1.5">
                                <Checkbox
                                  checked={checkedIds.has(row.positionId)}
                                  disabled={disabled}
                                  onCheckedChange={(checked) => toggleChecked(row.positionId, checked === true)}
                                />
                              </td>
                              <td className="px-2 py-1.5 max-w-[220px] truncate" title={position?.name || position?.beschreibung || ''}>
                                {position?.positionNumber ? `${position.positionNumber} ` : ''}
                                {position?.name || position?.beschreibung || '-'}
                              </td>
                              <td className="px-2 py-1.5 max-w-[260px] truncate" title={raw?.description}>
                                {raw?.description ||
                                  (row.outcome === 'suche-fehlgeschlagen'
                                    ? 'Suche fehlgeschlagen'
                                    : row.outcome === 'kein-treffer'
                                      ? 'kein Treffer'
                                      : '-')}
                                {frische && frische.stufe !== 'unbekannt' && (
                                  <span
                                    className={`ml-1.5 inline-block rounded border px-1 py-0 text-[10px] align-middle ${FRISCHE_BADGE_CLASS[frische.stufe]}`}
                                    title={`Preisstand aus gültig-ab/aktualisiert-am${raw?.priceValidUntil ? ` · gültig bis ${new Date(raw.priceValidUntil).toLocaleDateString('de-DE')}` : ''}`}
                                  >
                                    {frische.label}
                                  </span>
                                )}
                                {row.blockReason && (
                                  <span
                                    className={`flex items-center gap-1 text-[11px] mt-0.5 ${
                                      row.outcome === 'suche-fehlgeschlagen' ? 'text-red-700' : 'text-amber-700'
                                    }`}
                                  >
                                    <AlertTriangle className="h-3 w-3 flex-shrink-0" />
                                    {row.blockReason}
                                  </span>
                                )}
                                {rejectedIds.has(row.positionId) && (
                                  <span className="flex items-center gap-1 text-[11px] mt-0.5 text-red-700">
                                    <AlertTriangle className="h-3 w-3 flex-shrink-0" />
                                    Übernahme abgelehnt — Kalkulation bitte einzeln prüfen
                                  </span>
                                )}
                                {/* P1-3: sichtbarer Konflikt-Hinweis, wie im Einzeldialog — der Kandidat
                                    ist deshalb NICHT vorausgewählt (aber weiterhin manuell anhakbar). */}
                                {row.unitsIncompatible && (
                                  <span className="flex items-center gap-1 text-[11px] mt-0.5 text-amber-700">
                                    <AlertTriangle className="h-3 w-3 flex-shrink-0" />
                                    Einheiten evtl. inkompatibel — nicht automatisch vorausgewählt
                                  </span>
                                )}
                                {/* P1-2: Suche für diese Position war abgeschnitten (mehr Treffer vorhanden, als
                                    geliefert) — das Ranking lief auf einem unvollständigen Set. */}
                                {row.truncated && (
                                  <span className="flex items-center gap-1 text-[11px] mt-0.5 text-amber-700">
                                    <AlertTriangle className="h-3 w-3 flex-shrink-0" />
                                    Weitere Treffer möglich — nicht automatisch vorausgewählt
                                  </span>
                                )}
                              </td>
                              <td className="px-2 py-1.5 text-right whitespace-nowrap font-medium">
                                {row.effektiverEk !== null ? formatEuro(row.effektiverEk) : '-'}
                              </td>
                              <td className="px-2 py-1.5">
                                {row.best && (
                                  <Badge className={ZONE_BADGE_CLASS[row.best.zone]}>{ZONE_LABEL[row.best.zone]}</Badge>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter className="flex-shrink-0 gap-2">
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isApplying}>
            Abbrechen
          </Button>
          <Button
            onClick={handleApply}
            disabled={!rows || isSearching || isApplying || checkedApplicableCount === 0}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            {isApplying ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Übernehme...</>
            ) : (
              <><Upload className="h-4 w-4 mr-2" />{checkedApplicableCount} Position(en) übernehmen</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default CatalogBatchSuggestDialog;
